/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, ClassNames } from '@emotion/react'
import { CSSProperties, useEffect, useState } from 'react'
import { lighten, TableCell, TableRow } from '@mui/material'
import { TableRowProps } from 'react-table'
import { noop } from 'lodash'
import cn from 'classnames'
import { Maybe } from '@vendorflow/types'
import { EnhancedRow } from '../../../types'
import BodyCell from './BodyCell'

interface Props<D extends object> {
  ExpandedComponent: ((props: { data: D }) => JSX.Element) | null | undefined
  row: EnhancedRow<D> // make sure this row is prepared before passing it in
  rowProps: Omit<TableRowProps, 'key'>
  innerRef?: ((element?: Element | undefined) => void) | undefined
  style?: CSSProperties
  className?: string
  measure?: () => void
  getRowClassName?: Maybe<(rowId: string) => string>
  onContextMenu?: (evt: React.MouseEvent<HTMLDivElement, MouseEvent>, data: D) => void
}

export default function BodyRow<D extends object>({
  ExpandedComponent,
  row,
  rowProps,
  innerRef,
  style,
  className: rootClassName,
  measure,
  getRowClassName,
  onContextMenu,
}: Props<D>) {
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const { className, style: rowPropsStyle, ...restOfRowProps } = rowProps
  const { isExpanded, isSelected } = row as EnhancedRow<D>

  useEffect(() => {
    if (!element) return

    const resizeObserver = new ResizeObserver(measure ? measure : noop)
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [!!element])

  return (
    <ClassNames>
      {({ css, cx, theme }) => (
        <div
          ref={(node) => {
            setElement(node)
            if (innerRef) {
              innerRef(node as Element | undefined)
            }
          }}
          className={rootClassName}
          css={cx(style && css(style as unknown as TemplateStringsArray))}
        >
          <TableRow
            {...restOfRowProps}
            component="div"
            css={cx(
              // @ts-ignore
              css(rowPropsStyle),
              css`
                && {
                  display: flex;
                  flex-direction: row;
                  flex-wrap: nowrap;
                  align-items: center;
                  box-sizing: border-box;
                  border-bottom: 1px solid rgba(224, 224, 224, 1);
                  background-color: ${isSelected
                    ? lighten((theme as any)?.palette?.primary?.light, 0.5) || lighten('hsla(321, 60%, 80%, 0.3)', 0.5)
                    : 'transparent'};
                }

                &&:hover {
                  background-color: ${lighten((theme as any)?.palette?.primary?.light, 0.5) ||
                  lighten('hsla(321, 60%, 80%, 0.3)', 0.5)};
                }
              `
            )}
            onContextMenu={onContextMenu ? (e) => onContextMenu(e, row.original) : undefined}
            className={cn(className, getRowClassName && getRowClassName(row.id))}
          >
            {row.cells.map((cell) => {
              const { key, ...restOfCellProps } = cell.getCellProps()
              return <BodyCell key={key} cell={cell} cellProps={restOfCellProps} />
            })}
          </TableRow>
          {isExpanded && !!ExpandedComponent && (
            <TableRow
              component="div"
              css={css`
                && {
                  display: flex;
                  flex-direction: row;
                  flex-wrap: nowrap;
                  align-items: center;
                  box-sizing: border-box;
                  min-width: 100%;
                  width: 100%;
                  border-bottom: 1px solid rgba(224, 224, 224, 1);
                }
              `}
            >
              <TableCell
                component="div"
                variant="body"
                css={css`
                  && {
                    display: block;
                    border-bottom: none;
                  }
                `}
              >
                {/** @ts-ignore */}
                <ExpandedComponent data={row.original} />
              </TableCell>
            </TableRow>
          )}
        </div>
      )}
    </ClassNames>
  )
}
